import HttpClient from '../../services/HttpClient'

/**
 * Cliente REST Viajes (D/E/G/F + itinerario multi-Han).
 * Base: /api/v1/travel
 */

export async function fetchPlayableHans() {
  const { data } = await HttpClient().get('/api/v1/travel/playable-hans')
  return Array.isArray(data?.hans) ? data.hans : []
}

export async function fetchTravelRoutes(characterId) {
  const { data } = await HttpClient().get('/api/v1/travel/routes', {
    params: { characterId },
  })
  return {
    originSlug: data?.originSlug || null,
    routes: Array.isArray(data?.routes) ? data.routes : [],
  }
}

/**
 * Preview de camino multi-Han.
 * @param {{ characterId: number, to?: string, via?: string[], from?: string, waypoints?: string[], preferredRouteType?: string }}
 */
export async function fetchTravelPath({
  characterId,
  to,
  via,
  from,
  waypoints,
  preferredRouteType = 'safe',
} = {}) {
  const params = { characterId, preferredRouteType }
  if (to) params.to = to
  if (from) params.from = from
  if (Array.isArray(via) && via.length) params.via = via.join(',')
  if (Array.isArray(waypoints) && waypoints.length) params.waypoints = waypoints.join(',')
  const { data } = await HttpClient().get('/api/v1/travel/path', { params })
  return {
    originSlug: data?.originSlug || null,
    destSlug: data?.destSlug || null,
    waypoints: Array.isArray(data?.waypoints) ? data.waypoints : [],
    preferredRouteType: data?.preferredRouteType || preferredRouteType,
    legs: Array.isArray(data?.legs) ? data.legs : [],
    hopCount: Number(data?.hopCount) || 0,
  }
}

export async function fetchActiveTravel(characterId) {
  const { data } = await HttpClient().get('/api/v1/travel/sessions/active', {
    params: { characterId },
  })
  return {
    session: data?.session ?? null,
    events: Array.isArray(data?.events) ? data.events : [],
    spawns: Array.isArray(data?.spawns) ? data.spawns : [],
    adventure: data?.adventure ?? null,
    opening: data?.opening ?? null,
    pendingHard: data?.pendingHard ?? null,
    provisionSummary: data?.provisionSummary || null,
    itinerary: data?.itinerary || data?.session?.progress?.itinerary || null,
    pendingJoinRequests: Array.isArray(data?.pendingJoinRequests)
      ? data.pendingJoinRequests
      : [],
  }
}

/** Viaje activo de una crónica (para observadores / Master / estudiantes). */
export async function fetchActiveTravelByAdventure(adventureId) {
  const { data } = await HttpClient().get('/api/v1/travel/sessions/active-by-adventure', {
    params: { adventureId },
  })
  return {
    session: data?.session ?? null,
    events: Array.isArray(data?.events) ? data.events : [],
    spawns: Array.isArray(data?.spawns) ? data.spawns : [],
    adventure: data?.adventure ?? null,
    opening: data?.opening ?? null,
    pendingHard: data?.pendingHard ?? null,
    provisionSummary: data?.provisionSummary || null,
    itinerary: data?.itinerary || data?.session?.progress?.itinerary || null,
    proposal: data?.proposal ?? null,
    roster: Array.isArray(data?.roster) ? data.roster : [],
    joinCount: data?.joinCount ?? null,
    pendingCount: data?.pendingCount ?? null,
    canStart: data?.canStart ?? null,
    pendingJoinRequests: Array.isArray(data?.pendingJoinRequests)
      ? data.pendingJoinRequests
      : [],
  }
}

export async function requestJoinTravel(sessionId, characterId) {
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/join-request`, {
    characterId,
  })
  return data
}

export async function acceptJoinTravelRequest(requestId, characterId = null) {
  const body = {}
  if (characterId != null) body.characterId = characterId
  const { data } = await HttpClient().post(
    `/api/v1/travel/join-requests/${requestId}/accept`,
    body
  )
  return data
}

export async function rejectJoinTravelRequest(requestId, characterId = null) {
  const body = {}
  if (characterId != null) body.characterId = characterId
  const { data } = await HttpClient().post(
    `/api/v1/travel/join-requests/${requestId}/reject`,
    body
  )
  return data
}

export async function joinTravelProposal(proposalId, characterId) {
  const { data } = await HttpClient().post(`/api/v1/travel/proposals/${proposalId}/join`, {
    characterId,
  })
  return data
}

/** Quedarse en la zona: abandona la aventura. */
export async function leaveTravelProposal(proposalId, characterId) {
  const { data } = await HttpClient().post(`/api/v1/travel/proposals/${proposalId}/leave`, {
    characterId,
  })
  return data
}

/** Solo el proponente: expulsa no unidos e inicia el reloj. */
export async function startTravelProposal(proposalId, characterId) {
  const { data } = await HttpClient().post(`/api/v1/travel/proposals/${proposalId}/start`, {
    characterId,
  })
  return data
}

export async function startTravel(
  characterId,
  {
    destSlug,
    waypoints,
    via,
    routeType = 'safe',
    preferredRouteType,
    provisionTier,
    parentAdventureId,
    openingNarrative,
  } = {}
) {
  const { data } = await HttpClient().post('/api/v1/travel/sessions', {
    characterId,
    destSlug,
    waypoints,
    via,
    routeType,
    preferredRouteType: preferredRouteType || routeType,
    provisionTier,
    parentAdventureId,
    openingNarrative,
  })
  return data
}

export async function cancelTravel(sessionId, characterId = null) {
  const body = {}
  if (characterId != null) body.characterId = characterId
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/cancel`, body)
  return data
}

/** Pausa manual del viaje. */
export async function pauseTravel(sessionId, characterId = null) {
  const body = {}
  if (characterId != null) body.characterId = characterId
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/pause`, body)
  return data
}

/**
 * Dejar el viaje (quedarse en el Han). Pausa para despedida; el último cierra el viaje.
 */
export async function leaveTravelParty(sessionId, characterId) {
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/leave-party`, {
    characterId,
  })
  return data
}

/**
 * Reanudar viaje.
 * Manual/leave/join: dueño o Master. Hard: solo Master / travel.gm (el API rechaza al dueño).
 */
export async function resumeTravel(sessionId, { characterId = null, eventId } = {}) {
  const body = {}
  if (characterId != null) body.characterId = characterId
  if (eventId != null) body.eventId = eventId
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/resume`, body)
  return data
}

export async function claimTravelSpawn(sessionId, spawnId, characterId) {
  const { data } = await HttpClient().post(
    `/api/v1/travel/sessions/${sessionId}/spawns/${spawnId}/claim`,
    { characterId }
  )
  return data
}

export async function submitHardResolution(sessionId, characterId, { text, replyId } = {}) {
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/hard-resolution`, {
    characterId,
    text,
    replyId,
  })
  return data
}

export async function fetchTravelGmQueue({ limit, mine } = {}) {
  const params = {}
  if (limit != null) params.limit = limit
  if (mine) params.mine = 1
  const { data } = await HttpClient().get('/api/v1/travel/gm/queue', {
    params: Object.keys(params).length ? params : undefined,
  })
  return Array.isArray(data?.items) ? data.items : []
}

export async function approveTravelHard(sessionId, { eventId } = {}) {
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/hard-approve`, {
    eventId,
  })
  return data
}

export async function rejectTravelHard(sessionId, { eventId, note } = {}) {
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/hard-reject`, {
    eventId,
    note,
  })
  return data
}

export async function forceCloseTravel(sessionId) {
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/force-close`)
  return data
}

export async function injectTravelHard(sessionId) {
  const { data } = await HttpClient().post(`/api/v1/travel/sessions/${sessionId}/events`, {
    type: 'hard',
  })
  return data
}
